import type { ReactNode } from 'react';
import {
  coreExtensionData,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import catalogPlugin from '@backstage/plugin-catalog/alpha';

import {
  __testOnly_CatalogEntityKindSwitch,
  type FallbackWrapper,
  type KindRoute,
} from './catalog-entity-kind-switch';

export type { FallbackWrapper, KindRoute } from './catalog-entity-kind-switch';

/**
 * Override the catalog entity page so that specific entity kinds render
 * a dedicated page component while all other kinds fall back to the
 * framework default. Matching is case-insensitive; the first entry in the
 * routes array wins on duplicate kinds.
 *
 * When `fallback` is provided, kinds without a `KindRoute` match render
 * the framework's entity element as children of `fallback`. Without it,
 * the framework element renders directly, including its native header.
 *
 * NFS resolution is last-module-wins (silent overwrite). Register only a
 * SINGLE catalog-entity-kind override module app-wide, otherwise earlier
 * overrides are dropped without warning. See
 * scratch/evh3-research-findings.md § Q1a for source-verified evidence.
 */
export function createCatalogEntityKindOverride(
  routes: KindRoute[],
  fallback?: FallbackWrapper,
) {
  const CatalogEntityKindSwitch = __testOnly_CatalogEntityKindSwitch(
    routes,
    fallback,
  );

  const override = catalogPlugin.getExtension('page:catalog/entity').override({
    factory(originalFactory) {
      return (function* () {
        for (const entry of originalFactory()) {
          yield entry.id === coreExtensionData.reactElement.id
            ? coreExtensionData.reactElement(
                <CatalogEntityKindSwitch fallback={entry.value as ReactNode} />,
              )
            : entry;
        }
      })();
    },
  });

  return createFrontendModule({
    extensions: [override],
    pluginId: 'catalog',
  });
}
